<template>
  <div class="gold-scheme-page-wrapper m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Page Title & Identity -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-circle-multiple-outline</v-icon>
          </div>
          <div>
            <div class="page-title">GOLD SAVINGS SCHEMES</div>
            <div class="page-subtitle">Configure recurring investment plans, interest parameters &amp; customer enrollment</div>
          </div>
        </div>

        <!-- Right: Action Bar (Search + Refresh + Actions) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <div class="search-input-wrap">
            <v-text-field
              v-model="keyword"
              clearable
              background-color="white"
              color="#9a6b27"
              hide-details
              placeholder="Search schemes by name..."
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              class="search-input"
            ></v-text-field>
          </div>

          <v-btn
            icon
            small
            title="Refresh Directory"
            class="refresh-icon-btn"
            @click="getSchemeList()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <v-btn
            outlined
            color="#475569"
            class="btn-secondary text-capitalize"
            @click="$router.push('/CustomerSchemeList')"
          >
            <v-icon left small color="#475569">mdi-bookmark-outline</v-icon>
            Enlisted Customers
          </v-btn>

          <v-btn
            outlined
            color="#9a6b27"
            class="btn-gold-outlined text-capitalize"
            @click="openAddCustomerModal()"
          >
            <v-icon left small color="#9a6b27">mdi-account-plus-outline</v-icon>
            New Customer
          </v-btn>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="openAddSchemeModal()"
          >
            <v-icon left small>mdi-plus</v-icon>
            New Scheme
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- 1. Total Schemes -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">ACTIVE SCHEMES</div>
          <div class="kpi-value">{{ totalSchemesCount }}</div>
          <div class="kpi-hint">Configured savings plans</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-gold</v-icon>
        </div>
      </div>

      <!-- 2. Min Monthly Deposit -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">MIN. MONTHLY DEPOSIT</div>
          <div class="kpi-value">₹{{ formatCurrency(minMonthlyDeposit) }}</div>
          <div class="kpi-hint">Starting entry installment</div>
        </div>
        <div class="kpi-icon-pill" style="background: #ecfdf5; color: #059669;">
          <v-icon size="20" color="#059669">mdi-cash-multiple</v-icon>
        </div>
      </div>

      <!-- 3. Max Yearly Interest -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">MAX. ANNUAL INTEREST</div>
          <div class="kpi-value">{{ maxYearlyInterest }}% <span class="kpi-unit">p.a.</span></div>
          <div class="kpi-hint">Highest scheme return</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
          <v-icon size="20" color="#ca8a04">mdi-trending-up</v-icon>
        </div>
      </div>

      <!-- 4. Avg Monthly Rate -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">AVG. MONTHLY RATE</div>
          <div class="kpi-value">{{ avgMonthlyInterest }}%</div>
          <div class="kpi-hint">Weighted monthly factor</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fdf4ff; color: #a855f7;">
          <v-icon size="20" color="#a855f7">mdi-percent-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Master Data Table / Content Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <!-- Data Table View -->
      <div v-if="schemeList && schemeList.length > 0">
        <v-simple-table class="pro-master-table m-stack-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 55px;">S.No.</th>
                <th class="text-left" style="min-width: 260px;">Scheme Details</th>
                <th class="text-right" style="width: 200px;">Monthly Deposit</th>
                <th class="text-right" style="width: 190px;">Yearly Interest</th>
                <th class="text-right" style="width: 190px;">Monthly Interest</th>
                <th class="text-center" style="width: 140px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(value, i) in schemeList"
                :key="value._id || i"
                class="master-table-row"
              >
                <td data-label="S.No." class="text-muted font-weight-medium">
                  {{ page == 1 ? i + 1 : i + 1 + 20 * (page - 1) }}
                </td>
                <td data-label="Scheme">
                  <div class="d-flex align-center">
                    <div class="scheme-avatar mr-3">
                      <v-icon size="16" color="#ca8a04">mdi-gold</v-icon>
                    </div>
                    <div>
                      <div class="scheme-name-text">
                        {{ value.goldSchemeName || '—' }}
                      </div>
                      <div class="scheme-subtext">
                        Recurring Gold Savings Account
                      </div>
                    </div>
                  </div>
                </td>
                <td data-label="Monthly Deposit" class="text-right">
                  <div class="deposit-text font-weight-bold">
                    ₹{{ formatCurrency(value.minmonthlydeposit) }}
                  </div>
                  <div class="text-caption text-muted">Per month installment</div>
                </td>
                <td data-label="Yearly Interest" class="text-right">
                  <span v-if="value.yearlyIntrest" class="interest-chip yearly">
                    <v-icon x-small color="#15803d" class="mr-1">mdi-arrow-up-thin</v-icon>
                    {{ value.yearlyIntrest }}% p.a.
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td data-label="Monthly Interest" class="text-right">
                  <span v-if="value.monthlyIntrest !== undefined && value.monthlyIntrest !== null" class="interest-chip monthly">
                    {{ Number(value.monthlyIntrest).toFixed(4) }}% p.m.
                  </span>
                  <span v-else class="text-muted">0.0000%</span>
                </td>
                <td data-label="Actions" class="text-center">
                  <div class="d-flex align-center justify-center" style="gap: 6px;">
                    <v-btn
                      icon
                      x-small
                      title="View Enlisted Subscribers"
                      class="action-icon-btn view-btn"
                      @click="$router.push('/CustomerSchemeList')"
                    >
                      <v-icon size="15" color="#0284c7">mdi-account-group-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      title="Edit Scheme Parameters"
                      class="action-icon-btn edit-btn"
                      @click="openEditScheme(value)"
                    >
                      <v-icon size="15" color="#475569">mdi-pencil-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      title="Delete Scheme"
                      class="action-icon-btn delete-btn"
                      @click="openDeleteScheme(value._id)"
                    >
                      <v-icon size="15" color="#dc2626">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <!-- Footer & Pagination -->
        <div class="table-footer-wrap px-4 py-3 d-flex flex-wrap align-center justify-space-between border-t">
          <div class="text-caption text-muted">
            Showing {{ ((page - 1) * 20) + 1 }} to {{ Math.min(page * 20, totalSchemesCount) }} of {{ totalSchemesCount }} schemes
          </div>
          <v-pagination
            v-if="Pagelength > 1"
            v-model="page"
            :length="Pagelength"
            :total-visible="7"
            color="#9a6b27"
            dense
            class="pro-pagination"
          ></v-pagination>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="32" color="#94a3b8">mdi-gold</v-icon>
        </div>
        <div class="empty-title">No gold schemes found</div>
        <div class="empty-subtitle">
          {{ keyword ? 'No savings schemes match your search query. Try another keyword.' : 'Get started by creating your first gold scheme plan.' }}
        </div>
        <div class="mt-4" v-if="keyword">
          <v-btn small outlined color="#64748b" @click="keyword = ''; getSchemeList();">
            <v-icon left small>mdi-close</v-icon>
            Clear Filter
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- ===== CREATE NEW SCHEME MODAL ===== -->
    <v-dialog v-model="addScheme" max-width="580px" persistent>
      <v-form ref="addcat" v-model="addcat" @submit.prevent>
        <v-card class="modal-card rounded-lg overflow-hidden">
          <!-- Header -->
          <div class="modal-header px-6 py-4 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <div class="modal-icon-pill mr-3" style="background: #fefce8; color: #9a6b27;">
                <v-icon size="18" color="#9a6b27">mdi-plus-circle-outline</v-icon>
              </div>
              <div>
                <div class="modal-title">Create New Gold Scheme</div>
                <div class="modal-subtitle">Define monthly deposit requirements &amp; annual yield percentage</div>
              </div>
            </div>
            <v-btn icon small @click="closeAddSchemeModal()" class="close-btn">
              <v-icon small color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Body -->
          <div class="px-6 py-4">
            <v-row dense>
              <v-col cols="12">
                <label class="field-label">Scheme Name <span class="required-star">*</span></label>
                <v-text-field
                  v-model="schemeName"
                  placeholder="e.g. Swarna Varsha 11-Month, Golden Harvest"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6" class="mt-2">
                <label class="field-label">Monthly Deposit (₹) <span class="required-star">*</span></label>
                <v-text-field
                  v-model="monthlyDeposit"
                  type="number"
                  placeholder="e.g. 1000"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6" class="mt-2">
                <label class="field-label">Yearly Interest (%) <span class="required-star">*</span></label>
                <v-text-field
                  v-model="yearlyInterest"
                  type="number"
                  placeholder="e.g. 12"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Actions -->
          <div class="modal-footer px-6 py-3 border-t d-flex justify-end" style="gap: 8px;">
            <v-btn text class="btn-cancel" @click="closeAddSchemeModal()">Cancel</v-btn>
            <v-btn
              color="#9a6b27"
              class="btn-primary text-capitalize elevation-1"
              @click="AddScheme()"
              :loading="appLoading"
            >
              <v-icon left small>mdi-check</v-icon>
              Save Scheme
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- ===== EDIT SCHEME MODAL ===== -->
    <v-dialog v-model="editdialogue" max-width="580px" persistent>
      <v-form ref="editcat" v-model="editcat" @submit.prevent>
        <v-card class="modal-card rounded-lg overflow-hidden">
          <!-- Header -->
          <div class="modal-header px-6 py-4 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <div class="modal-icon-pill mr-3" style="background: #f1f5f9; color: #475569;">
                <v-icon size="18" color="#475569">mdi-pencil-outline</v-icon>
              </div>
              <div>
                <div class="modal-title">Edit Gold Scheme</div>
                <div class="modal-subtitle">Update scheme name, deposit terms, or interest rates</div>
              </div>
            </div>
            <v-btn icon small @click="closeEditSchemeModal()" class="close-btn">
              <v-icon small color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Body -->
          <div class="px-6 py-4">
            <v-row dense>
              <v-col cols="12">
                <label class="field-label">Scheme Name <span class="required-star">*</span></label>
                <v-text-field
                  v-model="curItem.goldSchemeName"
                  placeholder="Scheme Name"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6" class="mt-2">
                <label class="field-label">Monthly Deposit (₹) <span class="required-star">*</span></label>
                <v-text-field
                  v-model="curItem.minmonthlydeposit"
                  type="number"
                  placeholder="Monthly Deposit"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>

              <v-col cols="12" sm="6" class="mt-2">
                <label class="field-label">Yearly Interest (%) <span class="required-star">*</span></label>
                <v-text-field
                  v-model="curItem.yearlyIntrest"
                  type="number"
                  placeholder="Yearly Interest"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Actions -->
          <div class="modal-footer px-6 py-3 border-t d-flex justify-end" style="gap: 8px;">
            <v-btn text class="btn-cancel" @click="closeEditSchemeModal()">Cancel</v-btn>
            <v-btn
              color="#9a6b27"
              class="btn-primary text-capitalize elevation-1"
              @click="editScheme()"
              :loading="appLoading"
            >
              <v-icon left small>mdi-check</v-icon>
              Update Scheme
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- ===== DELETE SCHEME MODAL ===== -->
    <v-dialog v-model="deletedialog" max-width="440px">
      <v-card class="modal-card rounded-lg pa-6">
        <div class="text-center">
          <div class="delete-icon-box mb-3 mx-auto">
            <v-icon size="26" color="#dc2626">mdi-trash-can-outline</v-icon>
          </div>
          <div class="delete-title mb-1">Delete Scheme Plan</div>
          <div class="delete-subtitle">
            Are you sure you want to remove this savings scheme? Existing subscriber records will be archived.
          </div>
        </div>
        <div class="d-flex align-center justify-end mt-6" style="gap: 8px;">
          <v-btn text class="btn-cancel" @click="deletedialog = false">Cancel</v-btn>
          <v-btn
            color="#dc2626"
            dark
            class="text-capitalize elevation-1 rounded-lg"
            @click="deleteScheme()"
            :loading="appLoading"
          >
            <v-icon left small>mdi-delete</v-icon>
            Confirm Delete
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ===== ADD NEW CUSTOMER MODAL ===== -->
    <v-dialog v-model="addcustomerdialogue" max-width="820px" persistent>
      <v-form ref="addcustomerform" @submit.prevent>
        <v-card class="modal-card rounded-lg overflow-hidden">
          <!-- Modal Header -->
          <div class="modal-header px-6 py-4 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <div class="modal-icon-pill mr-3" style="background: #fefce8; color: #9a6b27;">
                <v-icon size="18" color="#9a6b27">mdi-account-plus-outline</v-icon>
              </div>
              <div>
                <div class="modal-title">Create Retail Customer Profile</div>
                <div class="modal-subtitle">Register client for scheme enrollment and billing</div>
              </div>
            </div>
            <v-btn icon small @click="closeCustomerModal()" class="close-btn">
              <v-icon small color="#64748b">mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Modal Body -->
          <div class="px-6 py-4">
            <div class="form-section-title mb-3">Profile &amp; Contact</div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <label class="field-label">Customer Name <span class="required-star">*</span></label>
                <v-text-field
                  v-model="newCustomer"
                  placeholder="Full customer name"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="[rules.required]"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <label class="field-label">Phone Number</label>
                <v-text-field
                  v-model="newContact"
                  placeholder="10-digit mobile number"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="phoneRules"
                  type="number"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="form-section-title mb-3 mt-3">Address &amp; Location</div>
            <v-row dense class="mb-2">
              <v-col cols="12" sm="6">
                <label class="field-label">Address</label>
                <v-textarea
                  v-model="newAddress"
                  placeholder="Street address or locality"
                  rows="3"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-textarea>
              </v-col>
              <v-col cols="12" sm="6">
                <v-row dense>
                  <v-col cols="12">
                    <label class="field-label">City</label>
                    <v-text-field
                      v-model="newCity"
                      placeholder="City / Town"
                      outlined
                      dense
                      color="#9a6b27"
                      hide-details
                      class="pro-input"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <label class="field-label">Pin Code</label>
                    <v-text-field
                      v-model="newpinNumber"
                      placeholder="6-digit PIN"
                      type="number"
                      outlined
                      dense
                      color="#9a6b27"
                      :rules="pinRules"
                      hide-details="auto"
                      class="pro-input"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <div class="form-section-title mb-3 mt-3">Statutory KYC &amp; Financials</div>
            <v-row dense>
              <v-col cols="12" sm="4">
                <label class="field-label">PAN Number</label>
                <v-text-field
                  v-model="newCustomerPAN"
                  placeholder="e.g. ABCDE1234F"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="panRules"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Aadhaar Number</label>
                <v-text-field
                  v-model="newCustomerAadhar"
                  placeholder="12-digit Aadhaar"
                  outlined
                  dense
                  color="#9a6b27"
                  :rules="aadhaarRules"
                  type="number"
                  hide-details="auto"
                  class="pro-input"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <label class="field-label">Credit Limit (₹)</label>
                <v-text-field
                  v-model="newCreditLimit"
                  placeholder="0 = No limit enforced"
                  type="number"
                  outlined
                  dense
                  color="#9a6b27"
                  hide-details
                  class="pro-input"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Actions -->
          <div class="modal-footer px-6 py-3 border-t d-flex justify-end" style="gap: 8px;">
            <v-btn text class="btn-cancel" @click="closeCustomerModal()">Cancel</v-btn>
            <v-btn
              color="#9a6b27"
              class="btn-primary text-capitalize elevation-1"
              @click="validCustomer()"
              :loading="appLoading"
            >
              <v-icon left small>mdi-check</v-icon>
              Save Customer
            </v-btn>
          </div>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      page: 1,
      Pagelength: 1,
      totalCount: 0,
      jewelleryId: null,

      keyword: "",
      schemeList: [],
      curItem: {},
      curId: null,

      // Create Scheme
      addScheme: false,
      addcat: false,
      schemeName: "",
      monthlyDeposit: null,
      yearlyInterest: null,

      // Edit Scheme
      editdialogue: false,
      editcat: false,
      deletedialog: false,

      // Add Customer
      addcustomerdialogue: false,
      newCustomer: null,
      newCustomerPAN: null,
      newCustomerAadhar: null,
      newAddress: null,
      newCity: null,
      newpinNumber: null,
      newContact: null,
      newCreditLimit: null,

      // Validation Rules
      rules: {
        required: (value) => !!value || "Required field.",
        counter: (value) => (value && value.length <= 20) || "Max 20 characters",
      },
      phoneRules: [
        (v) => !v || v.length === 10 || "Invalid phone number (must be 10 digits).",
        (v) => !v || /^\d{10}$/.test(v) || "Phone number must contain digits only.",
      ],
      aadhaarRules: [
        (value) => !value || /^\d{12}$/.test(value) || "Invalid Aadhaar number (12 digits).",
      ],
      panRules: [
        (value) =>
          !value || /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value) || "Invalid PAN number format (e.g. ABCDE1234F).",
      ],
      pinRules: [
        (value) => !value || /^\d{6}$/.test(value) || "Invalid PIN code (6 digits).",
      ],
    };
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    totalSchemesCount() {
      return this.schemeList ? this.schemeList.length : 0;
    },
    minMonthlyDeposit() {
      if (!this.schemeList || !this.schemeList.length) return 0;
      const deposits = this.schemeList
        .map((s) => Number(s.minmonthlydeposit) || 0)
        .filter((d) => d > 0);
      return deposits.length ? Math.min(...deposits) : 0;
    },
    maxYearlyInterest() {
      if (!this.schemeList || !this.schemeList.length) return 0;
      const interests = this.schemeList.map((s) => Number(s.yearlyIntrest) || 0);
      return interests.length ? Math.max(...interests) : 0;
    },
    avgMonthlyInterest() {
      if (!this.schemeList || !this.schemeList.length) return "0.0000";
      const valid = this.schemeList.filter(
        (s) => s.monthlyIntrest !== undefined && s.monthlyIntrest !== null
      );
      if (!valid.length) return "0.0000";
      const sum = valid.reduce((acc, curr) => acc + Number(curr.monthlyIntrest || 0), 0);
      return (sum / valid.length).toFixed(4);
    },
  },
  watch: {
    keyword() {
      this.page = 1;
      this.getSchemeList();
    },
    page() {
      this.getSchemeList();
    },
  },
  mounted() {
    this.getSchemeList();
  },
  methods: {
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    openAddSchemeModal() {
      this.schemeName = "";
      this.monthlyDeposit = null;
      this.yearlyInterest = null;
      this.addScheme = true;
    },
    closeAddSchemeModal() {
      this.addScheme = false;
      if (this.$refs.addcat) {
        this.$refs.addcat.resetValidation();
      }
    },
    openEditScheme(value) {
      this.curItem = JSON.parse(JSON.stringify(value));
      this.editdialogue = true;
    },
    closeEditSchemeModal() {
      this.editdialogue = false;
      if (this.$refs.editcat) {
        this.$refs.editcat.resetValidation();
      }
    },
    openDeleteScheme(id) {
      this.curId = id;
      this.deletedialog = true;
    },
    openAddCustomerModal() {
      this.addcustomerdialogue = true;
    },
    closeCustomerModal() {
      this.addcustomerdialogue = false;
      this.resetCustomerForm();
    },
    resetCustomerForm() {
      this.newCustomer = null;
      this.newCustomerPAN = null;
      this.newCustomerAadhar = null;
      this.newAddress = null;
      this.newCity = null;
      this.newpinNumber = null;
      this.newContact = null;
      this.newCreditLimit = null;
      if (this.$refs.addcustomerform) {
        this.$refs.addcustomerform.resetValidation();
      }
    },
    getSchemeList() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/goldscheme/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          keyword: this.keyword,
          page: this.page,
          count: 20,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.schemeList = response.data.data || [];
            this.Pagelength = Math.ceil((response.data.count || this.schemeList.length) / 20) || 1;
          } else {
            this.showsnackbar = true;
            this.msg = response.data.msg;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    AddScheme() {
      if (this.$refs.addcat.validate()) {
        this.appLoading = true;
        axios({
          url: "/goldscheme/add",
          method: "POST",
          data: {
            goldSchemeName: this.schemeName,
            minmonthlydeposit: this.monthlyDeposit,
            yearlyIntrest: this.yearlyInterest,
          },
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
        })
          .then((response) => {
            this.appLoading = false;
            if (response.data.status === true) {
              this.msg = response.data.msg || "Gold Scheme created successfully!";
              this.showsnackbar = true;
              this.addScheme = false;
              this.schemeName = "";
              this.monthlyDeposit = null;
              this.yearlyInterest = null;
              this.getSchemeList();
            } else {
              this.msg = response.data.msg;
              this.showsnackbar = true;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.error(err);
          });
      }
    },
    editScheme() {
      if (this.$refs.editcat.validate()) {
        this.appLoading = true;
        axios({
          url: "/goldscheme/edit",
          method: "POST",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
          data: {
            id: this.curItem._id,
            yearlyIntrest: this.curItem.yearlyIntrest,
            goldSchemeName: this.curItem.goldSchemeName,
            minmonthlydeposit: this.curItem.minmonthlydeposit,
          },
        })
          .then((response) => {
            this.editdialogue = false;
            this.appLoading = false;
            if (response.data.status === true) {
              this.msg = response.data.msg || "Scheme updated successfully!";
              this.showsnackbar = true;
              this.getSchemeList();
            } else {
              this.msg = response.data.msg;
              this.showsnackbar = true;
            }
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.error(err);
          });
      }
    },
    deleteScheme() {
      this.appLoading = true;
      axios({
        url: "/goldscheme/delete",
        method: "GET",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: this.curId,
        },
      })
        .then((response) => {
          this.deletedialog = false;
          this.appLoading = false;
          if (response.data.status === true) {
            this.msg = response.data.msg || "Scheme deleted successfully!";
            this.showsnackbar = true;
            this.getSchemeList();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    validCustomer() {
      if (!this.newCustomer) {
        this.msg = "Please enter customer name.";
        this.showsnackbar = true;
        return;
      }
      this.AddCustomer();
    },
    AddCustomer() {
      this.appLoading = true;
      const jId =
        this.jewelleryId ||
        (this.appUser &&
          (this.appUser.jewelleryId ? this.appUser.jewelleryId._id || this.appUser.jewelleryId : null));

      axios({
        url: "/customer/add",
        method: "POST",
        data: {
          customerName: this.newCustomer,
          panNo: this.newCustomerPAN,
          aadharNo: this.newCustomerAadhar,
          address: this.newAddress,
          city: this.newCity,
          mobileNumber: this.newContact,
          pinNumber: this.newpinNumber,
          creditLimit: this.newCreditLimit,
          jewelleryId: jId,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.addcustomerdialogue = false;
            this.msg = response.data.msg || "Customer added successfully!";
            this.showsnackbar = true;
            this.resetCustomerForm();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap");

.gold-scheme-page-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.search-input-wrap {
  width: 260px;
}

@media (max-width: 960px) {
  .search-input-wrap {
    width: 100%;
  }
}

.search-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
}

.search-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.search-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.refresh-icon-btn {
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  background: #ffffff !important;
  height: 38px !important;
  width: 38px !important;
}

.refresh-icon-btn:hover {
  background: #f8fafc !important;
}

.btn-secondary {
  height: 38px !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  border-color: #cbd5e1 !important;
  background: #ffffff !important;
}

.btn-secondary:hover {
  background: #f8fafc !important;
}

.btn-gold-outlined {
  height: 38px !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  border-color: #9a6b27 !important;
  background: #fffdf9 !important;
}

.btn-gold-outlined:hover {
  background: #fefce8 !important;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

/* 4 KPI Metrics Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Master Data Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.pro-master-table table {
  width: 100%;
  border-collapse: collapse;
}

.pro-master-table th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
}

.pro-master-table td {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.master-table-row:hover {
  background-color: #f8fafc !important;
}

.scheme-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #fefce8;
  border: 1px solid #fef08a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.scheme-name-text {
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
}

.scheme-subtext {
  font-size: 11px;
  color: #64748b;
  margin-top: 1px;
}

.deposit-text {
  color: #0f172a;
  font-size: 13.5px;
}

.interest-chip {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}

.interest-chip.yearly {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.interest-chip.monthly {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
  font-family: monospace;
}

.action-icon-btn {
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  width: 28px !important;
  height: 28px !important;
}

.action-icon-btn:hover {
  background: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
}

.action-icon-btn.view-btn:hover {
  background: #f0f9ff !important;
  border-color: #bae6fd !important;
}

.action-icon-btn.edit-btn:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.action-icon-btn.delete-btn:hover {
  background: #fef2f2 !important;
  border-color: #fca5a5 !important;
}

.border-t {
  border-top: 1px solid #f1f5f9;
}

.text-muted {
  color: #94a3b8;
}

/* Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

/* Modal Dialogs */
.modal-card {
  background: #ffffff !important;
}

.modal-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.modal-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.modal-subtitle {
  font-size: 12px;
  color: #64748b;
}

.modal-icon-pill {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.form-section-title {
  font-size: 11.5px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
}

.required-star {
  color: #dc2626;
}

.pro-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 6px !important;
  font-size: 13px !important;
}

.pro-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.pro-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
}

.btn-cancel {
  border-radius: 8px !important;
  color: #64748b !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}

.delete-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fef2f2;
  border: 1px solid #fecaca;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.delete-subtitle {
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
  max-width: 360px;
  margin: 0 auto;
}
</style>